import React, { useState, useEffect, useRef } from 'react';
import { Typography, Row, Col, Tabs, Layout, Button, Flex, message } from 'antd';
import { DoubleLeftOutlined, RightOutlined } from '@ant-design/icons';

import { useRealmObjectContext } from '../contexts/RealmObjectProvider';
import { useRealmContext } from '../contexts/RealmProvider';
import { useAppContext } from '../contexts/AppProvider';
import { useAuthContext } from '../contexts/AuthProvider';

import IconifiedTitle from './IconifiedTitle';
import StyledTabContent from './StyledTabContent';
import FoundryTab from './FoundryTab';
import RealmObjectActionBar from './RealmObjectActionBar';
import PresentationTab from './PresentationTab';
import EmptyControlContent from './EmptyControlContent';
import ShareTab from './ShareTab';
import LinksPanel from './LinksPanel';
import ContextTab from './ContextTab';
import CustomSpinner from './CustomSpinner';

import { useAttributeManagement } from '../hooks/useAttributeManagement';
import { useRealmObjectCrud } from '../hooks/useRealmObjectCrud';
import { useRealmObjectFetching } from '../hooks/useRealmObjectFetching';
import ApiCaller from '../services/ApiCaller';
import { useLinkManagement } from '../hooks/useLinkManagement';
import APIContext from '../types/APIContext';
import PortalConfiguration from '../configuration/config';
import useFabrication from '../hooks/useFabricate';
import useAccessControl from '../hooks/useAccessControl';

import '../App.css';
import './css/RealmObject.css';

const { Sider, Content } = Layout;
const { Text } = Typography;

const RealmObject = () => {
  const [focusedInput, setFocusedInput] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [isLinksVisible, setIsLinksVisible] = useState(false);

  const { featureFlags } = useAppContext();
  const { realmID } = useRealmContext();
  const { sessionID, username, userPermissionLevel } = useAuthContext();
  const { deleteAccess } = useAccessControl();

  const {
    realmobjectDragTarget,
    setRealmObjectDragTarget,
    realmObjectID,
    realmObject,
    realmObjectList,
    updateRealmObject,
    setRealmObjectList,
    setRealmObjectID,
    setRealmObjectEdited,
    updateLinkedObjects,
    realmObjectAPICaller,
    isLoadingObject,
    allowedAccessors,
    allowedCampaigns
  } = useRealmObjectContext();

  const { addContextAttribute, removeContextAttribute } = useAttributeManagement();
  const { saveRealmObject, deleteRealmObject } = useRealmObjectCrud(
    realmObjectAPICaller,
    sessionID,
    username,
    realmID
  );
  const { fetchRealmObjectList } = useRealmObjectFetching(
    realmObjectAPICaller,
    sessionID,
    username,
    ApiCaller.handleApiError
  );
  const { createLink } = useLinkManagement();
  const { randomText } = useFabrication(sessionID, realmID, username);

  const linkApiContext = new APIContext(
    new ApiCaller(PortalConfiguration.linkURL),
    sessionID,
    realmID,
    ApiCaller.handleApiError
  );

  // 🔗 Builder registered by PresentationTab (returns { htmlFragment, plain })
  const presentationBuilderRef = useRef(null);

  const toggleSidePanel = () => setIsLinksVisible((v) => !v);

  const handleQuestionChange = (newValue, label) => {
    const updated = (realmObject?.payload?.Questions || []).map((q) =>
      q.Label === label ? { ...q, CurrentValue: newValue } : q
    );
    updateRealmObject('Questions', updated);
  };

  const handleAddAttribute = (attribute) => {
    if (realmObject) addContextAttribute(attribute.name, attribute.value, attribute.notes, realmObject);
  };

  const handleDeleteAttribute = (name) => {
    removeContextAttribute(name, realmObject);
  };

  const handleSave = async () => {
    if (!realmID || !realmObjectID) {
      console.error('Missing RealmID or RealmObjectID');
      return;
    }
    await saveRealmObject(realmObject);
    const updatedList = (realmObjectList || []).map((item) =>
      item.RealmObjectID === realmObjectID ? realmObject : item
    );
    setRealmObjectList(updatedList);
    setRealmObjectEdited(false);
    fetchRealmObjectList(realmID, true);
  };

  const handleDelete = async () => {
    if (!realmID || !realmObjectID) {
      console.error('Missing RealmID or RealmObjectID');
      return;
    }
    await Promise.all((allowedAccessors || []).map((u) => deleteAccess(u.accessId, u.targetId, true)));
    await Promise.all((allowedCampaigns || []).map((a) => deleteAccess(a.accessId, a.targetId, true)));
    await deleteRealmObject(realmObjectID);
    setRealmObjectID(null);
    fetchRealmObjectList(realmID, false);
  };

  const handleTitleChange = (newValue) => updateRealmObject('name', newValue);

  const handleDrop = (info) => {
    info.preventDefault();
    const dragObject = realmobjectDragTarget;
    if (!dragObject) return;
    const SK = dragObject.key;
    const PK = realmObjectID;
    if (SK !== PK) {
      createLink(PK, SK, linkApiContext);
      updateLinkedObjects();
      setRealmObjectDragTarget(null);
    } else {
      message.info("You can Love Yourself but you can't Link Yourself!");
    }
  };

  const handleDragOver = (info) => info.preventDefault();

  const processRandomText = async (textDescription) => randomText(textDescription);

  const getRandomDescription = async () => {
    const templateID = realmObject?.payload?.template?.templateID;
    const templateDescription = realmObject?.payload?.templateDescription;
    return `A name for table top rpg world's ${templateID} \n\n Described as ${templateDescription}`;
  };

  const iconifiedTitleProps =
    realmObject && {
      title: realmObject.name || 'unnamed',
      iconFileIndex: realmObject.payload.iconFileIndex,
      iconIndex: realmObject.payload.iconIndex,
      iconDisplaySize: 45,
      titleSize: 'large',
      titleEditable: true,
      iconEditable: true,
      randomTextFunction: processRandomText,
      randomDescriptionFunction: getRandomDescription,
      onChange: handleTitleChange,
      onIconChange: (iconFileIndex, iconIndex) => {
        updateRealmObject('iconFileIndex', iconFileIndex);
        updateRealmObject('iconIndex', iconIndex);
      }
    };

  const tabProps = [
    { text: 'Context', textType: 'large', textStyle: 'orange', backgroundClassName: 'custom-tab-background' },
    {
      text: 'Foundry',
      iconFileIndex: 31,
      iconIndex: 0,
      iconDisplaySize: 30,
      iconPosition: 'right',
      textType: 'large',
      textStyle: 'orange',
      backgroundClassName: 'custom-tab-background'
    },
    { text: 'Presentation', textType: 'large', textStyle: 'orange', backgroundClassName: 'custom-tab-background' },
    {
      text: 'Share',
      textType: 'large',
      textStyle: 'orange',
      backgroundClassName: 'custom-tab-background',
      highlight: realmObject?.publishedLevelACL === 'PUBLIC'
    }
  ];

  const isDisabled = !(realmID && realmObjectID);

  useEffect(() => {
    setQuestions(realmObject?.payload?.Questions || []);
    setAttributes(
      Array.isArray(realmObject?.payload?.ContextAttribute) ? realmObject.payload.ContextAttribute : []
    );
  }, [realmObject]);

  // ---------- Clipboard helpers ----------
  const copyPlainText = (plain) => {
    if (
      navigator.clipboard &&
      (window.isSecureContext || ['localhost', '127.0.0.1'].includes(window.location.hostname))
    ) {
      navigator.clipboard.writeText(plain).then(
        () => message.warning('Copied as plain text (enable HTTPS for formatted HTML).'),
        () => message.error('Copy blocked. Allow clipboard or use HTTPS.')
      );
      return;
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = plain;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      ok
        ? message.warning('Copied as plain text (enable HTTPS for formatted HTML).')
        : message.error('Copy blocked. Allow clipboard or use HTTPS.');
    } catch {
      message.error('Copy blocked. Allow clipboard or use HTTPS.');
    }
  };

  const legacyCopyHTML = (html, plain) => {
    try {
      const ghost = document.createElement('div');
      ghost.contentEditable = 'true';
      ghost.tabIndex = -1;
      ghost.style.position = 'fixed';
      ghost.style.left = '-9999px';
      ghost.style.top = '0';
      ghost.style.opacity = '0';
      ghost.style.pointerEvents = 'none';
      ghost.innerHTML = html;

      document.body.appendChild(ghost);
      ghost.focus();

      const sel = window.getSelection();
      sel.removeAllRanges();
      const range = document.createRange();
      range.selectNodeContents(ghost);
      sel.addRange(range);

      const ok = document.execCommand('copy');

      sel.removeAllRanges();
      document.body.removeChild(ghost);

      if (ok) {
        message.success('Copied HTML.');
        return true;
      }
      copyPlainText(plain);
      return false;
    } catch {
      copyPlainText(plain);
      return false;
    }
  };
  // ---------- /Clipboard helpers ----------

  // 🚩 Action bar handler
  const handleCopyHTML = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (!presentationBuilderRef.current) {
      message.error('Nothing to copy yet — open the Presentation tab once.');
      return;
    }

    // One unified builder for modern + legacy
    const { htmlFragment, plain } = await presentationBuilderRef.current();

    const isSecure =
      window.isSecureContext || ['localhost', '127.0.0.1'].includes(window.location.hostname);

    // Modern path (HTTPS/localhost)
    if (isSecure && navigator.clipboard && window.ClipboardItem) {
      try {
        const item = new window.ClipboardItem({
          'text/html': new Blob([htmlFragment], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' })
        });
        await navigator.clipboard.write([item]);
        message.success('Copied HTML.');
        return;
      } catch {
        // fall through to legacy
      }
    }

    // Legacy (works on non-secure hosts too)
    legacyCopyHTML(htmlFragment, plain);
  };

  // Tabs
  const items = [
    {
      key: '1',
      label: <StyledTabContent {...tabProps[2]} />,
      children: (
        <PresentationTab
          onRegisterCopyBuilder={(fn) => {
            // fn is an async function returning { htmlFragment, plain }
            presentationBuilderRef.current = fn;
          }}
        />
      )
    },
    {
      key: '2',
      label: <StyledTabContent {...tabProps[0]} />,
      children: (
        <ContextTab
          questions={questions}
          attributes={attributes}
          loading={isLoadingObject}
          handleQuestionChange={handleQuestionChange}
          focusedInput={focusedInput}
          setFocusedInput={setFocusedInput}
          handleAddAttribute={handleAddAttribute}
          handleDeleteAttribute={handleDeleteAttribute}
        />
      )
    },
    featureFlags.getUserFlag(userPermissionLevel, 'REALMOBJECT_SHARE_TAB') && {
      key: '3',
      label: <StyledTabContent {...tabProps[3]} />,
      children: <ShareTab />
    },
    {
      key: '4',
      label: <StyledTabContent {...tabProps[1]} />,
      children: <FoundryTab />
    }
  ].filter(Boolean);

  // No object loaded
  if (!realmObjectID) {
    return (
      <Row align="middle" justify="center" style={{ height: '100vh' }}>
        <Col>
          <EmptyControlContent emptyText="No Realm Object Loaded. Please select or create a Realm Object to continue." />
        </Col>
      </Row>
    );
  }

  // Regular content
  return (
    <Layout
      id="Realm_Object_Editor"
      droppable="true"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Row className="panel-background-medium" gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24} xl={24} className="realm-object-header">
          <Row justify="space-between" align="middle">
            <Col>
              <Flex className="title-spinner-container">
                <IconifiedTitle {...(iconifiedTitleProps || {})} />
                {isLoadingObject && <CustomSpinner />}
              </Flex>
            </Col>
            <Col>
              <RealmObjectActionBar
                isDisabled={!(realmID && realmObjectID)}
                handleDelete={handleDelete}
                handleSave={handleSave}
                handleCopyHTML={handleCopyHTML} // 👈 Copy in parent bar
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Layout className={`${realmObject?.publishedLevelACL === 'PUBLIC' ? 'public-highlight' : ''}`}>
        <Content className="panel-background-medium">
          {!isLinksVisible && (
            <Button
              id="toggle_links_panel"
              icon={<DoubleLeftOutlined />}
              onClick={toggleSidePanel}
              className="floating-links-button"
            >
              Links
            </Button>
          )}
          <Tabs
            defaultActiveKey="0"
            type="card"
            className="custom-tabs title-dark tabs-background"
            items={items}
          />
        </Content>

        <Sider
          width={isLinksVisible ? 300 : 0}
          className="realm-object-links-sider"
          collapsedWidth={0}
          collapsible
          collapsed={!isLinksVisible}
          trigger={null}
        >
          <div className="close-button-container">
            <Button type="text" icon={<RightOutlined />} onClick={toggleSidePanel} />
          </div>
          <div className="realm-object-links-panel-content">
            <LinksPanel />
          </div>
        </Sider>
      </Layout>
    </Layout>
  );
};

export default RealmObject;
